import { useMemo } from "react";
import { CountryConfig, resolveCountryConfig } from "@/helpers/countryConfig";
import { useGetCountriesQuery } from "@/redux/apis/resource/resourceApi";
import { useAppSelector } from "@/redux/hooks";
import { defaultCountrySelector } from "@/redux/slices/operator/selectors";

/**
 * Country-derived configuration for the current operator, or for an explicitly supplied country.
 *
 * Mirrors the shape of the mobile apps' `useCountryConfig` hook so the eventual swap there is a
 * one-file change.
 *
 * `config` is always defined: the API row is layered over bundled defaults, so there is no loading
 * state for a caller to handle. `isReady` says whether the API row has actually arrived, for the
 * rare caller that needs to know the difference.
 *
 * Note this resolves the country from `defaultCountrySelector` rather than `useOperator()`, which
 * *throws* when there is no active operator — that would take down guest routes (booking offer,
 * pay-invoice, job rating) and onboarding-before-a-provider-exists.
 */
export const useCountryConfig = (countryCodeOverride?: string) => {
  const operatorCountry = useAppSelector(defaultCountrySelector);
  const { data, isLoading, isFetching, refetch } = useGetCountriesQuery();

  const country = (countryCodeOverride || operatorCountry || "").toUpperCase();

  const config = useMemo<CountryConfig>(() => resolveCountryConfig(country ? data?.[country] : undefined, country), [data, country]);

  return {
    /** ISO country code in play; empty when neither an override nor an operator country is known. */
    country,
    /** Always defined — bundled defaults until the API row lands, then merged over them. */
    config,
    /** True once the API has answered for this country. */
    isReady: Boolean(country && data?.[country]),
    isLoading,
    isFetching,
    refetch,
  };
};

/**
 * Every country the backend knows about, for pickers.
 *
 * `bookable` intersects that with the operator's own bookable list, which arrives as an array on the
 * operator payload.
 */
export const useCountries = (bookableCountryList?: string[]) => {
  const { data, isLoading, isFetching } = useGetCountriesQuery();

  const countries = useMemo<CountryConfig[]>(() => Object.keys(data ?? {}).map((code) => resolveCountryConfig(data?.[code], code)), [data]);

  const bookable = useMemo<CountryConfig[]>(() => {
    if (!bookableCountryList?.length) return countries;

    const allowed = bookableCountryList.map((code) => code.toUpperCase());

    // Fall back to the bundled config for a bookable country the API has no row for, so a
    // misconfigured backend narrows the picker rather than emptying it.
    return allowed.map((code) => resolveCountryConfig(data?.[code], code)).filter((config) => Boolean(config.name));
  }, [countries, bookableCountryList, data]);

  return { countries, bookable, isReady: Boolean(data), isLoading, isFetching };
};
